import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useCompanyQuery } from '@/modules/companies/queries/useCompanyQuery';
import { getHMTenantSelection } from '@/modules/hiring-manager/utils/tenant';
import { CandidateNoticesSection } from '@/modules/legal/components/CandidateNoticesSection';
import { Button } from '@/modules/ui/components/button/Button';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';
import { useWorkspaceUsersQuery } from '@/modules/users/queries/useWorkspaceUsersQuery';

import { CareersPageSection } from './CareersPageSection';
import { InviteTeammateDialog } from './InviteTeammateDialog';
import { WorkspaceUserList } from './WorkspaceUserList';

/**
 * The tenant's own settings. Every Hiring Manager manages the candidate
 * notices; self-service tenants also see who is in the workspace and can
 * invite someone. Inviting opens a dialog so the page stays read-only until
 * the user acts.
 */
export function HMWorkspace() {
  const [searchParams] = useSearchParams();
  const tenantId = getHMTenantSelection(
    useCurrentUserQuery().data,
    searchParams,
  ).selectedTenantId;
  const companyQuery = useCompanyQuery(tenantId ?? '', {
    enabled: Boolean(tenantId),
  });
  const isSelfService = companyQuery.data?.isSelfServiceWorkspace === true;

  return (
    <div className="flex flex-col gap-11">
      {isSelfService ? (
        <WorkspaceRoster />
      ) : (
        <header>
          <h1 className="text-[24px] leading-8 font-semibold tracking-[-0.02em]">
            Workspace
          </h1>
          <p className="text-muted-foreground mt-1 text-[13px]">
            Settings for how Finlay contacts candidates for your organisation.
          </p>
        </header>
      )}

      {tenantId && (
        <div className="max-w-[720px]">
          <CandidateNoticesSection tenantId={tenantId} />
        </div>
      )}

      {tenantId && isSelfService && (
        <div className="max-w-[720px]">
          <CareersPageSection tenantId={tenantId} />
        </div>
      )}
    </div>
  );
}

/** Who is in a self-service workspace, with the invite that adds someone. */
function WorkspaceRoster() {
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const workspaceUsersQuery = useWorkspaceUsersQuery();
  const workspace = workspaceUsersQuery.data;
  const seatCapacity = workspace?.seatCapacity;
  const isAtSeatCapacity =
    seatCapacity?.limit != null && seatCapacity.used >= seatCapacity.limit;
  const openInvite = () => setIsInviteOpen(true);

  return (
    <div className="flex flex-col gap-8">
      <header className="flex max-w-[720px] flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[24px] leading-8 font-semibold tracking-[-0.02em]">
            Workspace
          </h1>
          <p className="text-muted-foreground mt-1 text-[13px]">
            Who sees your jobs and decides on candidates with you.
          </p>
        </div>
        {isAtSeatCapacity ? (
          <Button className="shrink-0" disabled size="sm" variant="outline">
            {seatCapacity.used}/{seatCapacity.limit} seats used
          </Button>
        ) : (
          <Button
            autoFocus
            className="shrink-0"
            onClick={openInvite}
            size="sm"
            variant="outline"
          >
            <Plus className="size-3.5 text-neutral-500" />
            Invite teammate
          </Button>
        )}
      </header>

      <WorkspaceUserList
        onInvite={openInvite}
        workspace={workspace}
        workspaceUsersQuery={workspaceUsersQuery}
      />

      <InviteTeammateDialog
        onOpenChange={setIsInviteOpen}
        open={isInviteOpen}
      />
    </div>
  );
}
